<template>
  <div class="flex m-8 justify-start flex-wrap">
    <div class="md:max-w-xs w-full mb-4 md:mb-0">
      <p class="pb-2 text-gray-500 font-medium">
        Información de tu proyecto
      </p>
      <p class="text-sm text-gray-400">
        Llena los datos del lugar en donde será construido tu proyecto.
      </p>
    </div>

    <div class="flex-1 md:ml-5">
      <div class="flex w-full flex-wrap">
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="project_name"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Nombre del proyecto</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="project_name"
              v-model.trim="$v.projectInfo.name.$model"
              type="text"
              required
              class="form-input block w-full sm:text-sm sm:leading-5"
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectInfo.name.$error,
              }"
            />
            <div
              v-if="$v.projectInfo.name.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="project_state"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Estado</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <select
              id="project_state"
              aria-label="Estado"
              v-model.trim="$v.projectInfo.state.$model"
              required
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectInfo.state.$error,
              }"
              class="form-select relative block w-full rounded-md bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
            >
              <option selected disabled>Selecciona un estado</option>
              <option v-for="state in states" :key="state">{{ state }}</option>
            </select>
            <div
              class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
              v-if="$v.projectInfo.state.$error"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="project_city"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Ciudad</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <select
              id="project_city"
              aria-label="Ciudad"
              v-model.trim="$v.projectInfo.city.$model"
              required
              :disabled="cityIsDisabled"
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectInfo.city.$error,
              }"
              class="form-select relative block w-full rounded-md bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
            >
              <option selected disabled>Selecciona una ciudad</option>
              <option
                v-for="city in filteredCities"
                :value="city.id"
                :key="city.id"
                >{{ city.city }}</option
              >
            </select>
            <div
              class="absolute inset-y-0 right-0 pr-8 flex items-center pointer-events-none"
              v-if="$v.projectInfo.city.$error"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="project_address"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Calle y número</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="project_address"
              v-model.trim="$v.projectInfo.address.$model"
              type="text"
              required
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectInfo.address.$error,
              }"
              class="form-input block w-full sm:text-sm sm:leading-5"
            />
            <div
              v-if="$v.projectInfo.address.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="project_town"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Colonia</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="project_town"
              v-model.trim="$v.projectInfo.town.$model"
              type="text"
              required
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectInfo.town.$error,
              }"
              class="form-input block w-full sm:text-sm sm:leading-5"
            />
            <div
              v-if="$v.projectInfo.town.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
        <div class="w-full lg:w-1/2 mb-4 lg:px-2">
          <label
            for="project_postal_code"
            class="block text-sm font-medium leading-5 text-gray-700"
            >Código postal</label
          >
          <div class="mt-1 relative rounded-md shadow-sm">
            <input
              id="project_postal_code"
              v-model.trim="$v.projectInfo.postal_code.$model"
              type="text"
              required
              :class="{
                'pr-10 border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
                  $v.projectInfo.postal_code.$error,
              }"
              class="form-input block w-full sm:text-sm sm:leading-5"
            />
            <div
              v-if="$v.projectInfo.postal_code.$error"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <svg
                class="h-5 w-5 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';

function inArray(array) {
  return function(value) {
    return array.includes(value);
  };
}

export default {
  props: {
    cities: { type: Array },
    states: { type: Array },
    projectInfo: {type: Object}
  },
  computed: {
    filteredCities() {
      if (this.projectInfo.state) {
        return this.cities.filter((city) => city.state.trim() === this.projectInfo.state.trim());
      }
      return this.cities;
    },
    cityIsDisabled() {
      return this.projectInfo.state ? false : true;
    },
  },
  validations() {
    return {
      projectInfo: {
        name: {
          required,
        },
        state: {
          required,
          inStatesArray: inArray(this.states),
        },
        city: {
          required,
          inCitiesArray: inArray([
            ...new Set(this.filteredCities.map((a) => a.id)),
          ]),
        },
        address: {
          required,
        },
        town: {
          required,
        },
        postal_code: {
          required,
        },
      },
    };
  },
  watch: {
    projectInfo: {
      deep: true,

      handler() {
        this.$emit('project-info-changed', this.projectInfo);
      },
    },
  },
};
</script>
